import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Loader2, Mail, CheckCircle, ArrowLeft } from "lucide-react";
import { portalFetch } from "@/lib/portalApi";
import SiteLink from "@/components/SiteLink";

export const Route = createFileRoute("/portal_/forgot-password")({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setError(null);

    try {
      await portalFetch("/api/v1/auth/forgot-password", {
        method: "POST",
        body: { email: email.trim() },
      });
      setSent(true);
    } catch (err: unknown) {
      const detail = (err as { details?: { detail?: string } })?.details
        ?.detail;
      if (detail?.includes("wait")) {
        setError(detail);
      } else {
        // Don't reveal whether email exists — show success regardless
        setSent(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col px-4 py-10 sm:px-6">
        <SiteLink
          href="/"
          className="inline-flex items-center gap-4 rounded-3xl border border-slate-200 bg-white px-4 py-2 shadow-sm transition hover:border-slate-300"
          style={{ width: "fit-content" }}
        >
          <img
            src="/Larasoft Logo-landscape.svg"
            alt="Larasoft"
            width={180}
            height={64}
            className="h-12"
            style={{ width: "auto" }}
          />
          <span className="rounded-full bg-brand-blue-light/20 px-3 py-1 text-xs font-bold uppercase tracking-[0.3em] text-brand-blue-dark">
            Client portal
          </span>
        </SiteLink>

        <div className="mt-12 flex justify-center">
          <Card className="w-full max-w-md">
            {sent ? (
              <>
                <CardHeader className="text-center">
                  <CheckCircle className="mx-auto h-12 w-12 text-green-600" />
                  <CardTitle className="mt-4">Check your email</CardTitle>
                  <CardDescription>
                    If an account exists for {email}, we've sent password reset
                    instructions. The link expires in 1 hour.
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <SiteLink href="/portal/sign-in">
                    <Button variant="outline">
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back to Sign In
                    </Button>
                  </SiteLink>
                </CardContent>
              </>
            ) : (
              <>
                <CardHeader className="text-center">
                  <Mail className="mx-auto h-12 w-12 text-brand-blue-medium" />
                  <CardTitle className="mt-4">Forgot your password?</CardTitle>
                  <CardDescription>
                    Enter your email address and we'll send you a link to reset
                    your password.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="reset-email">Email address</Label>
                      <Input
                        id="reset-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.currentTarget.value)}
                        placeholder="you@company.com"
                        autoComplete="email"
                        disabled={loading}
                        required
                      />
                    </div>

                    {error && (
                      <p className="text-sm text-destructive">{error}</p>
                    )}

                    <Button
                      type="submit"
                      className="w-full"
                      disabled={loading || !email.trim()}
                    >
                      {loading && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Send Reset Link
                    </Button>

                    <div className="text-center">
                      <SiteLink
                        href="/portal/sign-in"
                        className="text-sm text-muted-foreground hover:underline"
                      >
                        <ArrowLeft className="mr-1 inline h-3 w-3" />
                        Back to Sign In
                      </SiteLink>
                    </div>
                  </form>
                </CardContent>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
